import type { ProjectResult, StaleCrawlInfo } from '../api/types';
import { formatTime, parseUtcDate } from './datetime';

export type InspectCoverageBucket = 'indexed' | 'excluded' | 'error';
export type CrawlVsLastmod = 'crawl_after' | 'lastmod_newer' | 'unknown';

export interface InspectDetail {
  url: string;
  success: boolean;
  resultId?: string;
  createdAt?: string;
  httpStatus?: number | null;
  bucket: InspectCoverageBucket;
  verdict?: string;
  coverageState?: string;
  robotsTxtState?: string;
  indexingState?: string;
  pageFetchState?: string;
  crawledAs?: string;
  lastCrawlTime?: string;
  staleCrawl?: StaleCrawlInfo | null;
  sitemapLastmod?: string;
  crawlVsLastmod: CrawlVsLastmod;
  googleCanonical?: string;
  userCanonical?: string;
  referringUrls: string[];
  sitemaps: string[];
  inspectLink?: string;
  errorDetail?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0,
  );
}

export function inspectCoverageBucket(
  success: boolean,
  verdict?: string,
  coverageState?: string,
): InspectCoverageBucket {
  if (!success) return 'error';
  if (verdict === 'PASS') return 'indexed';
  const coverage = (coverageState ?? '').toLowerCase();
  if (coverage.includes('indexed') && !coverage.includes('not indexed')) {
    return 'indexed';
  }
  return 'excluded';
}

export function compareCrawlAndLastmod(
  lastCrawl?: string,
  lastmod?: string,
): CrawlVsLastmod {
  if (!lastCrawl || !lastmod) return 'unknown';
  const crawl = parseUtcDate(lastCrawl).getTime();
  const mod = parseUtcDate(lastmod).getTime();
  if (Number.isNaN(crawl) || Number.isNaN(mod)) return 'unknown';
  return crawl >= mod ? 'crawl_after' : 'lastmod_newer';
}

export function toInspectDetail(item: ProjectResult): InspectDetail {
  const payload = asRecord(item.payload);
  const inspection = asRecord(payload?.inspection) ?? payload;
  const inspectionResult = asRecord(inspection?.inspectionResult);
  const indexStatus = asRecord(inspectionResult?.indexStatusResult);
  const verdict = asString(indexStatus?.verdict);
  const coverageState = asString(indexStatus?.coverageState) ?? item.detail ?? undefined;
  const lastCrawlTime = asString(indexStatus?.lastCrawlTime);
  const sitemapLastmod = asString(payload?.sitemapLastmod);

  return {
    url: item.url,
    success: item.success,
    resultId: item.id,
    createdAt: item.createdAt,
    httpStatus: item.httpStatus,
    bucket: inspectCoverageBucket(item.success, verdict, coverageState),
    verdict,
    coverageState,
    robotsTxtState: asString(indexStatus?.robotsTxtState),
    indexingState: asString(indexStatus?.indexingState),
    pageFetchState: asString(indexStatus?.pageFetchState),
    crawledAs: asString(indexStatus?.crawledAs),
    lastCrawlTime,
    staleCrawl: item.staleCrawl ?? null,
    sitemapLastmod,
    crawlVsLastmod: compareCrawlAndLastmod(lastCrawlTime, sitemapLastmod),
    googleCanonical: asString(indexStatus?.googleCanonical),
    userCanonical: asString(indexStatus?.userCanonical),
    referringUrls: asStringArray(indexStatus?.referringUrls),
    sitemaps: asStringArray(indexStatus?.sitemap),
    inspectLink: asString(inspectionResult?.inspectionResultLink),
    errorDetail: item.success ? undefined : item.detail ?? undefined,
  };
}

export function verdictLabel(verdict?: string): string {
  switch (verdict) {
    case 'PASS':
      return 'ایندکس‌شده';
    case 'FAIL':
      return 'ایندکس‌نیست';
    case 'NEUTRAL':
      return 'نامشخص';
    case 'PARTIAL':
      return 'جزئی';
    case 'VERDICT_UNSPECIFIED':
      return 'نامشخص';
    default:
      return verdict || '—';
  }
}

export function robotsLabel(state?: string): string {
  switch (state) {
    case 'ALLOWED':
      return 'مجاز';
    case 'DISALLOWED':
      return 'مسدود';
    default:
      return state || '—';
  }
}

/** GSC-style “Crawl allowed?” */
export function crawlAllowedLabel(state?: string): string {
  if (state === 'ALLOWED') return 'بله';
  if (state === 'DISALLOWED') return 'خیر';
  return state ? robotsLabel(state) : '—';
}

/** GSC-style “Indexing allowed?” */
export function indexingAllowedLabel(state?: string): string {
  if (!state) return '—';
  if (state === 'INDEXING_ALLOWED') return 'بله';
  if (state === 'BLOCKED_BY_META_TAG') return 'مسدود با meta';
  if (state === 'BLOCKED_BY_HTTP_HEADER') return 'مسدود با هدر';
  if (state === 'BLOCKED_BY_ROBOTS_TXT') return 'مسدود robots.txt';
  return state;
}

export function fetchLabel(state?: string): string {
  switch (state) {
    case 'SUCCESSFUL':
      return 'واکشی موفق';
    case 'SOFT_404':
      return 'Soft 404';
    case 'NOT_FOUND':
      return '۴۰۴';
    case 'ACCESS_DENIED':
      return 'دسترسی رد شد';
    case 'SERVER_ERROR':
      return 'خطای سرور';
    case 'REDIRECT_ERROR':
      return 'خطای ریدایرکت';
    case 'ACCESS_FORBIDDEN':
      return '۴۰۳';
    case 'BLOCKED_ROBOTS_TXT':
      return 'مسدود robots.txt';
    case 'INVALID_URL':
      return 'URL نامعتبر';
    default:
      return state || '—';
  }
}

export function crawledAsLabel(value?: string): string {
  if (value === 'MOBILE') return 'موبایل';
  if (value === 'DESKTOP') return 'دسکتاپ';
  return value || '—';
}

export function bucketLabel(bucket: InspectCoverageBucket): string {
  if (bucket === 'indexed') return 'ایندکس‌شده';
  if (bucket === 'excluded') return 'ایندکس‌نیست';
  return 'خطا';
}

/** توضیح کوتاه برای تولتیپ/راهنما وقتی در UI واژهٔ bucket آمده است. */
export const BUCKET_MEANING_FA =
  'bucket یعنی دستهٔ خلاصهٔ وضعیت ایندکس هر URL بعد از بازرسی: ایندکس‌شده، ایندکس‌نیست، یا خطا. جزئیات دقیق‌تر گوگل در فیلد coverageState است؛ bucket فقط همان خلاصهٔ سه‌حالته است.';

/** Matches history column «ایندکس‌شده / ایندکس‌نیست» (errors count as not indexed). */
export function inspectUrlsByIndexStatus(
  results: ProjectResult[],
  status: 'indexed' | 'notIndexed',
): string[] {
  const urls: string[] = [];
  for (const item of results) {
    const { bucket } = toInspectDetail(item);
    const matches =
      status === 'indexed' ? bucket === 'indexed' : bucket !== 'indexed';
    if (matches) urls.push(item.url);
  }
  return urls;
}

export function crawlVsLastmodLabel(value: CrawlVsLastmod): string | undefined {
  if (value === 'crawl_after') return 'خزش بعد از lastmod';
  if (value === 'lastmod_newer') return 'lastmod جدیدتر از خزش';
  return undefined;
}

export function formatInspectInstant(value?: string): string {
  return formatTime(value);
}
